import { getSyncedData, getDocumentFieldConfiguration } from '../../services/services';
import { indexFieldConfig } from '../../utils/fieldConfig';

// Resets every slice to its initialState (handled in store.js). Dispatched on
// login before the new user's data is written, so no field of the previous
// account can survive into the next one.
export const reset_store = () => ({ type: 'RESET_STORE' });

export const sign_In = (text) => async (dispatch) => {
  try {
    dispatch({
      type: 'signIn',
      payload: text
    });
  } catch (err) {
    return err;
  }
};

// Fetch the company-wide ERP coding arrays once (getSyncedData type=all) and
// store them in Redux so the HIL/canvas screen can reuse them instead of pulling
// glInformation on every get-document-by-id. The response is already in the same
// shape glInformation used (vendor as { id, name }, glDivision1..4, etc.), so no
// transform is needed. Returns the object (or null) so callers that need it
// synchronously (e.g. canvas on first load) can await the dispatch.
export const load_synced_data = (companyId) => async (dispatch) => {
  try {
    const res = await getSyncedData('all', 'getDocument', companyId);
    if (res?.status === 200) {
      const syncedData = res?.data?.payload?.data || {};
      dispatch({
        type: 'syncedData',
        payload: syncedData
      });
      return syncedData;
    }
    return null;
  } catch (err) {
    return null;
  }
};

// Clear the cached synced data (e.g. on logout) so the next login always loads
// fresh arrays for the account that logs in.
export const clear_synced_data = () => async (dispatch) => {
  try {
    dispatch({ type: 'clearSyncedData' });
  } catch (err) {
    return err;
  }
};

// The company's document field configuration, flattened to a name → enabled map and
// cached for the session. Every consumer that used to read one of the eight retired
// company permission flags reads this instead, so it is loaded next to the synced
// arrays: at login, after an ERP sync, and after the Configurations page saves.
// Returns the map so a caller that needs it immediately — getSyncArray at sign-in —
// can await it rather than racing the store.
export const load_field_config = (companyId) => async (dispatch) => {
  try {
    const res = await getDocumentFieldConfiguration(companyId);
    if (res?.status === 200) {
      const fieldConfig = indexFieldConfig(res?.data?.payload?.data);
      dispatch({
        type: 'fieldConfig',
        payload: fieldConfig
      });
      return fieldConfig;
    }
    return null;
  } catch (err) {
    return null;
  }
};

// Wipe on logout so a different account never reads the previous company's fields.
export const clear_field_config = () => async (dispatch) => {
  try {
    dispatch({ type: 'clearFieldConfig' });
  } catch (err) {
    return err;
  }
};

export const get_queue = (text) => async (dispatch) => {
  try {
    dispatch({
      type: 'queueData',
      payload: text
    });
  } catch (err) {
    return err;
  }
};

export const get_all_errors = (text) => async (dispatch) => {
  try {
    dispatch({
      type: 'allErrors',
      payload: text
    });
  } catch (err) {
    return err;
  }
};
export const get_all_users = (text) => async (dispatch) => {
  try {
    dispatch({
      type: 'allUsers',
      payload: text
    });
  } catch (err) {
    return err;
  }
};
export const get_pending_data = (text) => async (dispatch) => {
  try {
    dispatch({
      type: 'pendingData',
      payload: text
    });
  } catch (err) {
    return err;
  }
};

export const user_information = (text) => async (dispatch) => {
  try {
    dispatch({
      type: 'userInfo',
      payload: text
    });
  } catch (err) {
    return err;
  }
};
export const get_manual_users = (text) => async (dispatch) => {
  try {
    dispatch({
      type: 'manualUsers',
      payload: text
    });
  } catch (err) {
    return err;
  }
};
export const get_tabs = (text) => async (dispatch) => {
  try {
    dispatch({
      type: 'tabs',
      payload: text
    });
  } catch (err) {
    return err;
  }
};

export const get_all_vendors = (text) => async (dispatch) => {
  try {
    dispatch({
      type: 'allVendors',
      payload: text
    });
  } catch (err) {
    return err;
  }
};

export const get_fitered_obj = (text) => async (dispatch) => {
  try {
    dispatch({
      type: 'filteredObj',
      payload: text
    });
  } catch (err) {
    return err;
  }
};

export const get_analytics = (text) => async (dispatch) => {
  try {
    dispatch({
      type: 'analytics',
      payload: text
    });
  } catch (err) {
    return err;
  }
};

export const doc_counts = (text) => async (dispatch) => {
  try {
    dispatch({
      type: 'docCounts',
      payload: text
    });
  } catch (err) {
    return err;
  }
};

export const table_filters = (text) => async (dispatch) => {
  try {
    dispatch({
      type: 'tableFilters',
      payload: text
    });
  } catch (err) {
    return err;
  }
};

// Partial update that merges against the LATEST persisted tableFilters at
// dispatch time. Use this for changing a single slice (filter/sort/pagination)
// so sibling keys are never clobbered by a stale closure snapshot.
export const update_table_filters = (partial) => async (dispatch, getState) => {
  try {
    const current = getState()?.table?.tableFilters || {};
    dispatch({
      type: 'tableFilters',
      payload: { ...current, ...partial }
    });
  } catch (err) {
    return err;
  }
};

export const set_job_budgets = (text) => async (dispatch) => {
  try {
    dispatch({
      type: 'jobBudgets',
      payload: text
    });
  } catch (err) {
    return err;
  }
};

export const set_current_budget = (text) => async (dispatch) => {
  try {
    dispatch({
      type: 'currentBudget',
      payload: text
    });
  } catch (err) {
    return err;
  }
};

export const set_budget_summary = (text) => async (dispatch) => {
  try {
    dispatch({
      type: 'budgetSummary',
      payload: text
    });
  } catch (err) {
    return err;
  }
};

export const set_budget_loading = (text) => async (dispatch) => {
  try {
    dispatch({
      type: 'budgetLoading',
      payload: text
    });
  } catch (err) {
    return err;
  }
};

export const foundation_connection_details = (text) => async (dispatch) => {
  try {
    dispatch({
      type: 'foundationConnectionDetails',
      payload: text
    });
  } catch (err) {
    return err;
  }
};
